import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";


export type PublicNavbarActivePage = "home" | "pricing" | "resources" | "reviews" | "support";

interface PublicNavbarProps {
  activePage?: PublicNavbarActivePage;
}

const PublicNavbar = ({ activePage }: PublicNavbarProps) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const langWrapRef = useRef<HTMLDivElement | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState<"EN">("EN");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Scroll elevation
  useEffect(() => {
    const onScroll = () => {
      const nav = navRef.current;
      if (!nav) return;
      if (window.scrollY > 10) nav.classList.add("nav-elevated");
      else nav.classList.remove("nav-elevated");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close lang menu on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!langWrapRef.current) return;
      if (!langWrapRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // ESC to close + scroll lock
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const cls = (page: PublicNavbarActivePage) => (activePage === page ? "active" : undefined);

  return (
    <div className="tilprep-public-navbar">
      <nav ref={navRef}>
        <Link to="/" className="nav-logo">
          <img
            src="/navy_logo.svg"
            alt="TILPrep logo — TIL-I exam prep for Politecnico di Torino"
            width="76"
            height="95"
            loading="eager"
            decoding="sync"
          />
          <span>TILPrep</span>
        </Link>
        <ul className="nav-links">
          <li><Link to="/" className={cls("home")}>Home</Link></li>
          <li><Link to="/pricing" className={cls("pricing")}>Pricing</Link></li>
          <li><Link to="/resources" className={cls("resources")}>Resources</Link></li>
          <li><Link to="/reviews" className={cls("reviews")}>Reviews</Link></li>
          <li><Link to="/support" className={cls("support")}>Support</Link></li>
        </ul>
        <div className="nav-right">
          <div className={`nav-lang-wrap${langOpen ? " open" : ""}`} ref={langWrapRef}>
            <button className="nav-lang-btn" onClick={() => setLangOpen((o) => !o)}>
              <span className="nav-lang-current">{lang}</span>
              <svg className="nav-lang-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1l4 4 4-4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="nav-lang-menu">
              <button
                className={`nav-lang-option${lang === "EN" ? " active" : ""}`}
                onClick={() => { setLang("EN"); setLangOpen(false); }}
              >
                <span>EN</span><span className="check">✓</span>
              </button>
              <button
                className="nav-lang-option disabled"
                disabled
                aria-disabled="true"
                title="Italian coming soon"
              >
                <span>IT</span><span className="soon">SOON</span>
              </button>
            </div>
          </div>
          {user ? (
            <Link to="/dashboard" className="nav-auth-pill">Dashboard →</Link>
          ) : (
            <Link to="/login" className="nav-auth-pill">Log In / Sign Up</Link>
          )}
          <button
            className="mobile-hamburger"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </button>
        </div>
      </nav>
      <div
        className={`mobile-menu-backdrop${mobileOpen ? " open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <aside className={`mobile-menu${mobileOpen ? " open" : ""}`} aria-hidden={!mobileOpen}>
        <div className="mobile-menu-header">
          <button
            className="mobile-menu-close"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <ul className="mobile-menu-links">
          <li><Link to="/" className={cls("home")}>Home</Link></li>
          <li><Link to="/pricing" className={cls("pricing")}>Pricing</Link></li>
          <li><Link to="/resources" className={cls("resources")}>Resources</Link></li>
          <li><Link to="/reviews" className={cls("reviews")}>Reviews</Link></li>
          <li><Link to="/support" className={cls("support")}>Support</Link></li>
        </ul>
        <div className="mobile-menu-divider" />
        <div className="mobile-menu-lang">
          <button className={lang === "EN" ? "active" : undefined} onClick={() => setLang("EN")}>EN</button>
          <button className="disabled" disabled aria-disabled="true" title="Italian coming soon">IT · SOON</button>
        </div>
        {user ? (
          <Link to="/dashboard" className="mobile-menu-cta">Dashboard →</Link>
        ) : (
          <Link to="/login" className="mobile-menu-cta">Log In / Sign Up</Link>
        )}
      </aside>
    </div>
  );
};

export default PublicNavbar;